/** tm/components/TaskRegisterModal.tsx — 업무 등록 모달 (독립) */
import React, { useState, useRef, useEffect } from 'react';
import type { TaskTemplate, AIPreset, TaskCategory, UploadedFile, TaskDecisionItem, CollectStepConfig, AIStepConfig, AIBizplanConfig } from '../types';
import { C, T, R, Badge } from './ui';
import CollectLinkPicker from './CollectLinkPicker';
import CollectStepConfigPanel from './CollectStepConfigPanel';
import AIStepConfigPanel from './AIStepConfigPanel';
import AIBizplanConfigPanel from './AIBizplanConfigPanel';
import DueDatePicker from './DueDatePicker';
import TemplatePicker from './TemplatePicker';
import { TagEditor } from './TagEditor';
import * as api from '../api';
import { listProjects } from '../../venture/api';
import type { Row as VentureRow } from '../../venture/api';
import { OFFICES } from '../../venture/types';
import { STAFF, ownerOf } from '../flow/flowLogic';

// ── 디자인 토큰 (ConsultResultModal과 동일) ──
const S = {
  primary: '#111827',
  accent: '#2563EB',
  accentLt: '#EFF6FF',
  accentHover: '#245F99',
  title: '#111827',
  body: '#374151',
  sub: '#6B7280',
  muted: '#9CA3AF',
  sectionBg: '#F8FAFC',
  border: '#E5E7EB',
  borderDk: '#D1D5DB',
  white: '#FFFFFF',
  green: '#059669',
  greenLt: '#ECFDF5',
  red: '#DC2626',
  redLt: '#FEF2F2',
  purple: '#7C3AED',
  purpleLt: '#F3E8FF',
  orange: '#D97706',
} as const;

const STEP_TYPE_CONFIG: { key: string; label: string; icon: string; color: string; bg: string }[] = [
  { key: 'work',             label: '일반 작업',       icon: '⚙️', color: S.sub,    bg: '#F3F4F6' },
  { key: 'collect',          label: '자료수집',        icon: '📂', color: '#2563EB', bg: '#EFF6FF' },
  { key: 'ai_questionnaire', label: 'AI 질문지',      icon: '🤖', color: S.purple,  bg: S.purpleLt },
  { key: 'ai_bizplan',       label: 'AI 사업계획서',   icon: '📊', color: S.purple,  bg: S.purpleLt },
];
function getStepTypeInfo(key: string) {
  return STEP_TYPE_CONFIG.find(c => c.key === key) || STEP_TYPE_CONFIG[0];
}

const EMOJI_LIST = ['📋','📊','💼','🏢','📈','💰','🔧','📂','🎯','⭐','🔥','💡','🛡️','📑','🧾','✏️','🤝','📞','🗓️','⚡'];

/** 투자 처리 방법 빠른 고르기 — 벤처 등록 화면 예시와 같은 말(2026-10-16 · VentureRegister 「투자 처리 방법」) */
const INVEST_PLANS = ['전환사채 투자, 확인서 받은 뒤 바로 회수', '보통주로 그대로 유지', '대표님이 재인수', '미정'];

interface Props {
  title: string;
  companyId?: string;
  /** 벤처 GP 새 조합 이름 기본값(「○○개인투자조합1호」)에 쓴다 */
  companyName?: string;
  /** 열자마자 벤처 카드 하나(상담 결과 화면의 「🚀 벤처 GP / LP」 단추 · 2026-10-18) */
  seedVenture?: 'gp' | 'lp';
  /** 한 상담에 회사 여럿(관계사 · 2026-10-18) — 둘 이상이면 업무마다 「회사」를 고른다 · 첫 줄 = 주 회사 */
  companies?: { id: string; name: string }[];
  fpId?: string;
  consultationId?: string;
  showConsultFields?: boolean;
  consultContent?: string;
  consultFiles?: UploadedFile[];
  templates: TaskTemplate[];
  taskCategories: TaskCategory[];
  aiPresets: AIPreset[];
  onDone: (result: { content?: string; files?: UploadedFile[]; tags?: {required:string[];recommended:string[];interests:string[]}; decisions: TaskDecisionItem[]; autoStartCollect?: { dId: string; si: number } }) => void; // V22_COLLECT_START:22-Ba
  enableAutoStartCollect?: boolean; // V22_COLLECT_START:22-Ba
  onCancel: () => void;
  onToast: (msg: string) => void;
  modalMaxWidth?: number;
  modalMaxHeight?: string;
  initialDecisions?: TaskDecisionItem[];
  headerExtra?: React.ReactNode;
  panelMode?: boolean;
  initialMode?: 'template' | 'custom';
}

let _tdId = 0;
function nextId() { return 'tdr_' + (++_tdId); }


function IMEInput({ value, onChange, ...props }: { value: string; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [local, setLocal] = React.useState(value);
  const composing = React.useRef(false);
  React.useEffect(() => { if (!composing.current) setLocal(value); }, [value]);
  return <input value={local}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={e => { composing.current = false; onChange(e.currentTarget.value); }}
    onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
    {...props} />;
}

/** 템플릿 이름 고치기 줄의 작은 버튼 — 저장 · 취소 · 이름 고치기 같은 크기 (2026-10-26) */
const tplBtn: React.CSSProperties = { flexShrink: 0, height: 26, minWidth: 56, padding: '0 8px', borderRadius: 5, border: '0.5px solid #D1D5DB', background: '#fff', color: '#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };

export default function TaskRegisterModal({
  title, consultationId, showConsultFields, companyName, seedVenture, companyId, companies,
  consultContent: initContent, consultFiles: initFiles,
  templates: propTemplates, taskCategories = [], aiPresets = [],
  onDone, onCancel, onToast,
  modalMaxWidth, modalMaxHeight, initialDecisions, headerExtra,
  panelMode,
  initialMode,
  enableAutoStartCollect, // V22_COLLECT_START:22-Bb
}: Props) {
  // V22_COLLECT_START:22-Bb — autoStartCollect 옵션 보관용 ref
  const pendingAutoStartRef = React.useRef<{ dId: string; si: number } | null>(null);
  const [content, setContent] = useState(initContent || '');
  const [consultTags, setConsultTags] = useState<{required:string[];recommended:string[];interests:string[]}>({required:[],recommended:[],interests:[]});
  const [files, setFiles] = useState<UploadedFile[]>(initFiles || []);
  const [uploading, setUploading] = useState(false);
  const [decisions, setDecisions] = useState<TaskDecisionItem[]>(() => {
    if (initialDecisions && initialDecisions.length > 0) return initialDecisions;
    if (initialMode === 'template') return [{ id: nextId(), mode: 'template', templateId: '', customName: '', customIcon: '📋', taskStatus: '진행', deadline: '', steps: [], saveAsTemplate: false }];
    if (initialMode === 'custom') return [{ id: nextId(), mode: 'custom', templateId: '', customName: '', customIcon: '📋', taskStatus: '진행', deadline: '', steps: [{ name: '', type: 'work', due: '' }], saveAsTemplate: false }];
    return [];
  });
  // globalDeadline removed
  const [expandStepTypeMenu, setExpandStepTypeMenu] = useState<string | null>(null);
  const [expandEmojiPicker, setExpandEmojiPicker] = useState<string | null>(null);
  // 이 창에서 고치거나 새로 만든 템플릿(2026-10-29 지도사 「템플릿 검색 · 편집 · 추가도 여기서」) — 이 창에 바로 보이고 · tm:template-saved 로 다른 화면(TMStore)도 따라 바뀐다
  const [tplOver, setTplOver] = useState<Record<string, TaskTemplate>>({});
  const templates = React.useMemo(() => {
    const ids = new Set(propTemplates.map(t => t.id));
    return [...propTemplates.map(t => tplOver[t.id] || t), ...Object.values(tplOver).filter(t => !ids.has(t.id))];
  }, [propTemplates, tplOver]);
  const putTpl = (t: TaskTemplate) => {
    setTplOver(prev => ({ ...prev, [t.id]: t }));
    window.dispatchEvent(new CustomEvent<TaskTemplate>('tm:template-saved', { detail: t }));
  };
  // 템플릿 이름 고치기(2026-10-26 지도사 「템플릿명을 여기서도 수정 가능하게」)
  const [tplEdit, setTplEdit] = useState<{ dId: string; tplId: string; value: string; busy: boolean } | null>(null);
  const tplName = (t: TaskTemplate) => t.name;
  // 단계를 템플릿에도 반영 — 한 번 더 눌러야 저장(진행 중 업무의 아직 안 한 단계 안내도 따라 바뀌므로)
  const [tplPush, setTplPush] = useState<{ dId: string; busy: boolean } | null>(null);
  // 단계 이름 · 종류 · 기한 말고도 고친 것(자료수집 항목 · 질문지 · 사업계획서 설정 · 업무 유형) — 고른 뒤 손대면 표시(2026-10-29 지도사 「업무 템플릿도 변경하면 저장할 수 있게」)
  const [tplDirty, setTplDirty] = useState<Record<string, boolean>>({});
  const markDirty = (dId: string, on = true) => setTplDirty(p => (!!p[dId] === on ? p : { ...p, [dId]: on }));
  const saveTplName = async () => {
    if (!tplEdit || tplEdit.busy) return;
    const tpl = templates.find(t => t.id === tplEdit.tplId);
    const name = tplEdit.value.replace(/\s+/g, ' ').trim();
    if (!tpl) { setTplEdit(null); return; }
    const old = tplName(tpl);
    if (!name || name === old) { setTplEdit(null); return; }
    if (templates.some(t => t.id !== tpl.id && t.is_active && tplName(t) === name)) { onToast(`「${name}」 템플릿이 이미 있어요`); return; }
    setTplEdit({ ...tplEdit, busy: true });
    try {
      await api.updateTaskTemplate(tpl.id, { name });
      putTpl({ ...tpl, name });
      // 업무명이 옛 템플릿 이름 그대로였던 업무는 새 이름으로(따로 적은 업무명은 그대로)
      setDecisions(prev => prev.map(x => (x.templateId === tpl.id && x.customName.trim() === old ? { ...x, customName: name } : x)));
      setTplEdit(null);
      onToast(`템플릿 이름을 「${name}」(으)로 바꿨어요`);
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '이름을 바꾸지 못했어요');
      setTplEdit(prev => (prev ? { ...prev, busy: false } : prev));
    }
  };
  const [expandedStepConfig, setExpandedStepConfig] = useState<{ dId: string; si: number; type: string } | null>(null);
  const [stepConfigs, setStepConfigs] = useState<Record<string, { collect?: CollectStepConfig; ai?: AIStepConfig; bizplan?: AIBizplanConfig; link?: { id: string; label: string } }>>(() => {
    const init: Record<string, { collect?: CollectStepConfig; ai?: AIStepConfig; bizplan?: AIBizplanConfig; link?: { id: string; label: string } }> = {};
    (initialDecisions || []).forEach(d => {
      d.steps.forEach((s, si) => {
        const ext = (s as Record<string, unknown>).extras as Record<string, unknown> | undefined;
        if (!ext) return;
        const key = d.id + ':' + si;
        const entry: { collect?: CollectStepConfig; ai?: AIStepConfig; bizplan?: AIBizplanConfig; link?: { id: string; label: string } } = {};
        if (ext.collectConfig) entry.collect = ext.collectConfig as CollectStepConfig;
        if (ext.aiConfig) entry.ai = ext.aiConfig as AIStepConfig;
        if (ext.bizplanConfig) entry.bizplan = ext.bizplanConfig as AIBizplanConfig;
        if (Object.keys(entry).length > 0) init[key] = entry;
      });
    });
    return init;
  });
  const fileRef = useRef<HTMLInputElement>(null);

  // handleSave가 blur() 직후 setTimeout 콜백에서 옛 closure 값을 읽는 stale-closure 문제 방지.
  // blur로 인한 setState는 React가 flush한 뒤 effect로 ref를 갱신하므로, 콜백에서 ref를 읽으면 항상 최신값.
  const decisionsRef = useRef(decisions);
  const stepConfigsRef = useRef(stepConfigs);
  useEffect(() => { decisionsRef.current = decisions; }, [decisions]);
  useEffect(() => { stepConfigsRef.current = stepConfigs; }, [stepConfigs]);

  // ── decision helpers ──
  function addTemplateTask() {
    setDecisions(p => [...p, { id: nextId(), mode: 'template', templateId: '', customName: '', customIcon: '📋', taskStatus: '진행', deadline: '', steps: [], saveAsTemplate: false }]);
  }
  // ── 벤처인증(투자) — GP 새 조합 · LP 기존 조합 참여(또는 조합 미정 → LP 후보) (2026-10-18 지도사 「조합명은 다를 수도 · GP 로 새로 만들 수도 · 다른 조합에 LP 로 연동될 수도」)
  const [ventureRows, setVentureRows] = useState<VentureRow[] | null>(null);
  const needRows = decisions.some(d => !!d.venture);   // GP 도 — 이미 벤처에 등록한 조합이면 연결만(2026-10-18)
  useEffect(() => { if (needRows && ventureRows === null) listProjects().then(r => setVentureRows(r || [])).catch(() => setVentureRows([])); }, [needRows, ventureRows]);
  const multiCo = !!companies && companies.length > 1;
  const coIdOf = (d: TaskDecisionItem): string => d.companyId || companyId || (companies && companies[0] ? companies[0].id : '');
  const coNameOf = (d: TaskDecisionItem): string => (companies && companies.find(c => c.id === coIdOf(d)) || { name: companyName || '' }).name;
  const defaultOrg = (name: string = companyName || '') => String(name || '').replace(/\(\s*(주|유|합)\s*\)|주식회사|유한회사|㈜/g, '').replace(/\s+/g, '').trim() + '개인투자조합1호';
  // 업무 이름 — 가운뎃점 없이 사람이 적듯(2026-10-18 지도사 「이름 앞에 쩜 찍는 거 클로드 스타일」)
  const ventureName = (v: NonNullable<TaskDecisionItem['venture']>) => (v.role === 'gp' ? '벤처기업 확인 (투자형, GP)' : '벤처기업 확인 (투자형, LP)');   // 2026-10-18 지도사 「타이틀을 벤처기업 확인 (투자형, GP) · (투자형, LP) 이렇게」 — 조합 이름은 붙이지 않는다
  /** 이 회사가 이미 들어 있는 조합(벤처 화면에 등록한 것) — 업체 id 또는 이름으로 (2026-10-18 지도사 「이미 벤처 모듈에서 등록한 게 있으면 여기서 연결만」) */
  const normCo = (x: string) => String(x || '').replace(/\(\s*(주|유|합)\s*\)|주식회사|유한회사|㈜|\s/g, '').toLowerCase();
  const ownRows = (d: TaskDecisionItem): VentureRow[] => {
    const id = coIdOf(d), nm = normCo(coNameOf(d));
    return (ventureRows || []).filter(r => (r.company_ids || []).includes(id) || (!!nm && (r.company_names || []).some(n => normCo(n) === nm)));
  };
  // 이미 든 조합이 보이면 처음 한 번은 그 조합을 골라 둔다(새 조합을 만들지 않게) — 직접 「새 조합」을 누르면 그대로
  const autoPicked = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!ventureRows) return;
    decisions.forEach(d => {
      if (!d.venture || d.venture.projectId || autoPicked.current.has(d.id)) return;
      const own = ownRows(d);
      autoPicked.current.add(d.id);
      if (own.length) updVenture(d.id, { projectId: own[0].id, projectOrg: own[0].org });
    });
  }, [ventureRows, decisions]);   // eslint-disable-line react-hooks/exhaustive-deps
  function addVentureTask(role: 'gp' | 'lp') {
    const v: NonNullable<TaskDecisionItem['venture']> = role === 'gp' ? { role, org: companyName ? defaultOrg() : '', office: 'roum' } : { role, projectId: '', projectOrg: '', office: 'roum' };
    setDecisions(p => [...p, { id: nextId(), mode: 'custom', templateId: '', customName: ventureName(v), customIcon: '🚀', taskStatus: '진행', deadline: '', steps: [], saveAsTemplate: false, venture: v }]);
  }
  const seededRef = useRef(false);
  useEffect(() => { if (seedVenture && !seededRef.current) { seededRef.current = true; addVentureTask(seedVenture); } }, [seedVenture]);   // eslint-disable-line react-hooks/exhaustive-deps
  function updVenture(dId: string, patch: Partial<NonNullable<TaskDecisionItem['venture']>>) {
    setDecisions(p => p.map(d => { if (d.id !== dId || !d.venture) return d; const v = { ...d.venture, ...patch }; return { ...d, venture: v, customName: ventureName(v) }; }));
  }
  function addCustomTask() {
    setDecisions(p => [...p, { id: nextId(), mode: 'custom', templateId: '', customName: '', customIcon: '📋', taskStatus: '진행', deadline: '', steps: [{ name: '', type: 'work', due: '' }], saveAsTemplate: false }]);
  }
  function updDecision(id: string, key: string, value: unknown) {
    setDecisions(p => p.map(d => d.id === id ? { ...d, [key]: value } : d));
  }
  function removeDecision(id: string) { setDecisions(p => p.filter(d => d.id !== id)); }
  /** 업무 카드 맨 위 「회사」 — 관계사와 함께 상담했을 때만(2026-10-18) · 벤처 GP 조합 이름이 기본값 그대로면 새 회사 이름으로 */
  function setCompany(d: TaskDecisionItem, id: string) {
    setDecisions(p => p.map(x => {
      if (x.id !== d.id) return x;
      const next: TaskDecisionItem = { ...x, companyId: id };
      if (x.venture && x.venture.role === 'gp' && (x.venture.org || '') === defaultOrg(coNameOf(x))) {
        const nm = (companies || []).find(c => c.id === id);
        const v = { ...x.venture, org: nm ? defaultOrg(nm.name) : x.venture.org };
        return { ...next, venture: v, customName: ventureName(v) };
      }
      return next;
    }));
  }
  /** 업무 카드 맨 위 — 담당(늘 · 2026-10-18 · 비면 오재인) + 회사(관계사와 함께 상담했을 때만) */
  function coPick(d: TaskDecisionItem) {
    const sel = { padding: '4px 8px', borderRadius: 5, border: `0.5px solid ${S.border}`, fontSize: 12, background: S.white, color: S.body } as const;
    return (
      <div style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 8, background: '#F8FAFC', borderBottom: `0.5px solid ${S.border}` }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: S.sub, flexShrink: 0 }}>담당</span>
        <select value={d.who || (() => { const t = d.mode === 'template' ? templates.find(x => x.id === d.templateId) : undefined; return ownerOf({ type: t ? tplName(t) : d.customName, category_path: t ? (t as unknown as { cat_path?: unknown }).cat_path : undefined }); })()} onChange={e => updDecision(d.id, 'who', e.target.value)} style={{ ...sel, width: 96, flexShrink: 0 }}>
          {STAFF.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        {multiCo && companies ? (
          <>
            <span style={{ fontSize: 11, fontWeight: 700, color: S.sub, flexShrink: 0, marginLeft: 6 }}>회사 <span style={{ color: S.red }}>*</span></span>
            <select value={coIdOf(d)} onChange={e => setCompany(d, e.target.value)} style={{ ...sel, flex: 1, minWidth: 0 }}>
              {companies.map((c, i) => <option key={c.id} value={c.id}>{c.name}{i === 0 ? ' (주 회사)' : ''}</option>)}
            </select>
          </>
        ) : null}
      </div>
    );
  }

  /** 템플릿 단계 → 이 창의 단계(기한 = 오늘 + 템플릿 간격) — 고르기 · 「바꿨는지」 비교가 같이 쓴다 */
  function mapTplSteps(tpl: TaskTemplate) {
    return (tpl.steps || []).map(s => {
      // 정상 상대일수(1~3650)만 사용; 날짜값(예: 20260508) 등 비정상 저장분은 기한 미설정 처리
      const rawDays = Number(s.due_days);
      const days = Number.isFinite(rawDays) && rawDays > 0 && rawDays <= 3650 ? rawDays : 0;
      const d = new Date(); d.setDate(d.getDate() + days);
      const dateStr = days > 0 ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
      return { name: s.name, type: s.type, due: dateStr, settings: (s as unknown as Record<string, unknown>).settings as Record<string, unknown> | undefined };
    });
  }
  /** 고른 템플릿과 단계(이름 · 종류 · 기한 · 개수)가 달라졌나 */
  function stepsChanged(d: TaskDecisionItem, tpl: TaskTemplate): boolean {
    const a = mapTplSteps(tpl), b = d.steps;
    return a.length !== b.length || a.some((x, i) => x.name.trim() !== b[i].name.trim() || (x.type || 'work') !== (b[i].type || 'work') || (x.due || '') !== (b[i].due || ''));
  }
  /** 업무 유형 id 경로 → 이름 경로(템플릿은 이름으로 적는다) */
  function catNamesOf(ids: string[] | undefined): string[] {
    return (ids || []).map(id => taskCategories.find(c => c.id === id)).filter((c): c is TaskCategory => !!c).map(c => c.name);
  }
  /** 이 창의 단계 → 템플릿 단계 · base(고친 템플릿의 옛 단계)가 있으면 같은 이름 단계의 담당 · 안내 글 · 받을 서류 · 결과 말 · 설정을 그대로 이어 받는다 */
  function toTplSteps(d: TaskDecisionItem, cfgs: typeof stepConfigs, base: TaskTemplate['steps'] = []): TaskTemplate['steps'] {
    const used = new Set<number>();
    const today0 = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00').getTime();
    return d.steps.filter(x => x.name.trim()).map((x, i) => {
      let bi = base.findIndex((b, j) => !used.has(j) && b.name.trim() === x.name.trim());
      // 이름만 고친 단계 — 같은 자리 옛 단계의 이름이 지금 단계에 없으면 그 단계로 본다(담당 · 안내 글을 잃지 않게)
      if (bi < 0 && i < base.length && !used.has(i) && !d.steps.some(y => y.name.trim() === base[i].name.trim())) bi = i;
      if (bi >= 0) used.add(bi);
      const old = bi >= 0 ? base[bi] : undefined;
      const cfg = cfgs[configKey(d.id, d.steps.indexOf(x))];
      const settings: Record<string, unknown> = { ...((old && old.settings) || {}) };
      if (x.type === 'collect' && cfg && cfg.collect) { settings.collectItems = cfg.collect.items || []; settings.collectRefFiles = cfg.collect.refFiles || []; settings.collectRefLinks = cfg.collect.refLinks || []; }
      if (x.type === 'ai_questionnaire' && cfg && cfg.ai) settings.aiConfig = cfg.ai;
      if (x.type === 'ai_bizplan' && cfg && cfg.bizplan) settings.bizplanConfig = cfg.bizplan;
      const due_days = x.due ? Math.max(0, Math.round((new Date(x.due + 'T00:00:00').getTime() - today0) / 86400000)) || 0 : 0;
      return { ...(old || { note: '', collect_ids: [], use_ai: false }), name: x.name.trim(), type: x.type || 'work', due_days, sort_order: i, settings: Object.keys(settings).length > 0 ? settings : undefined };
    });
  }
  /** 이 업무에서 고친 단계를 템플릿에도(다음 업무부터 이 단계로) */
  async function pushToTemplate(d: TaskDecisionItem) {
    const tpl = templates.find(t => t.id === d.templateId);
    if (!tpl || (tplPush && tplPush.busy)) return;
    if (!d.steps.some(x => x.name.trim())) { onToast('단계를 하나 이상 적어 주세요'); return; }
    setTplPush({ dId: d.id, busy: true });
    try {
      const steps = toTplSteps(d, stepConfigsRef.current, tpl.steps || []);
      // 업무 유형도 바꿨으면 함께(비면 그대로)
      const catIds = (d.category_path || []).filter(Boolean);
      const cat = catIds.length ? { cat_path: catNamesOf(catIds), category_id: catIds[catIds.length - 1] } : {};
      const r = await api.updateTaskTemplate(tpl.id, { steps, ...cat });
      const row = (r && r.data) || { ...tpl, steps };
      putTpl({ ...tpl, ...cat, ...row, steps: row.steps || steps });
      setTplPush(null);
      markDirty(d.id, false);
      onToast(`「${tplName(tpl)}」 템플릿에 반영했어요 · 다음 업무부터 이 단계로 시작해요`);
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '템플릿에 반영하지 못했어요');
      setTplPush({ dId: d.id, busy: false });
    }
  }
  /** 「+ 새 템플릿 만들기」 — 이 칸을 직접 적기로 바꾸고 저장할 때 템플릿도 만든다 · 고르던 템플릿 단계는 그대로 이어서(복사해 새로 만들기) · 같은 이름이 있으면 그걸 고름 */
  function startNewTemplate(tdId: string, name: string) {
    const same = name ? templates.find(t => t.is_active && t.name.trim() === name) : undefined;
    if (same) { selectTemplate(tdId, same.id); onToast(`「${name}」 템플릿이 이미 있어서 골랐어요`); return; }
    setDecisions(p => p.map(d => (d.id !== tdId ? d : {
      ...d, mode: 'custom', templateId: '', saveAsTemplate: true,
      customName: name || (d.templateId ? `${d.customName.trim()} (새)` : d.customName),
      steps: d.steps.length ? d.steps : [{ name: '', type: 'work', due: '' }],
    })));
  }
  /** 직접 적던 칸을 다시 템플릿 고르기로 */
  function backToTemplate(tdId: string) {
    setDecisions(p => p.map(d => (d.id !== tdId ? d : { ...d, mode: 'template', templateId: '', saveAsTemplate: false, customName: '', steps: [] })));
  }

  function selectTemplate(tdId: string, tplId: string) {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    markDirty(tdId, false);
    const mappedSteps = mapTplSteps(tpl);
    // cat_path: 이름 배열 → ID 배열 변환
    const catIds: string[] = [];
    let parentId: string | null = null;
    for (const name of (tpl.cat_path || [])) {
      const cat = taskCategories.find(c => c.name === name && c.parent_id === parentId);
      if (cat) { catIds.push(cat.id); parentId = cat.id; }
      else break;
    }
    setDecisions(p => p.map(d => d.id === tdId ? {
      ...d, templateId: tplId, steps: mappedSteps,
      category_path: catIds,
      // [#3] 사용자가 이미 입력한 이름이 있으면 유지, 없으면 템플릿 이름으로 자동 채우기
      customName: d.customName?.trim() ? d.customName : tplName(tpl),
    } : d));
    // Pre-populate stepConfigs from template settings
    const newConfigs: Record<string, { collect?: CollectStepConfig; ai?: AIStepConfig; bizplan?: AIBizplanConfig; link?: { id: string; label: string } }> = {};
    mappedSteps.forEach((s, si) => {
      const raw = s.settings;
      if (!raw) return;
      const key = configKey(tdId, si);
      if (s.type === 'collect' && raw.collectItems) {
        const mappedCollectItems = (raw.collectItems as Record<string, unknown>[]).map(ci => ({
          ...ci,
          description: (ci.description as string) || (ci.desc as string) || '',
        }));
        newConfigs[key] = { collect: { items: mappedCollectItems as unknown as CollectStepConfig['items'], refFiles: [], refLinks: [], baseTemplateId: (raw.baseTemplateId as string) || '' } };
      }
      if (s.type === 'ai_questionnaire') {
        let aiFiles = Array.isArray(raw.files) ? raw.files as { label: string; name: string; s3_key: string }[] : [];
        let aiPrompt = (raw.prompt as string) || (raw.extraPrompt as string) || '';
        let aiInclude = !!(raw.includeCollect || raw.includeCollectAnswers || raw.include_collect_files);
        if (aiFiles.length === 0 && raw.presetId) {
          const pr = aiPresets.find(p => p.id === raw.presetId);
          if (pr) {
            const prRaw = pr as unknown as Record<string, unknown>;
            const prDocs = Array.isArray(pr.docs) && pr.docs.length > 0 ? pr.docs : (Array.isArray(prRaw.files) ? prRaw.files as {name:string;s3_key?:string}[] : []);
            aiFiles = prDocs.map((d: Record<string, unknown>) => ({ label: (d.label as string) || (d.name as string) || '', name: (d.name as string) || '', s3_key: (d.s3_key as string) || '' }));
            if (!aiPrompt) aiPrompt = pr.prompt || '';
            if (prRaw.include_collect_files) aiInclude = true;
          }
        }
        newConfigs[key] = { ai: {
          presetId: (raw.presetId as string) || '',
          files: aiFiles, extraPrompt: aiPrompt,
          includeCollectAnswers: aiInclude,
          phase: 'settings' as const, result: '',
        } };
      }
      if (s.type === 'ai_bizplan') {
        let bpFiles = Array.isArray(raw.files) ? raw.files as { label: string; name: string; s3_key: string }[] : [];
        let bpPrompt = (raw.prompt as string) || (raw.extraPrompt as string) || '';
        if (bpFiles.length === 0 && raw.presetId) {
          const pr = aiPresets.find(p => p.id === raw.presetId);
          if (pr) {
            const prRaw = pr as unknown as Record<string, unknown>;
            const prDocs = Array.isArray(pr.docs) && pr.docs.length > 0 ? pr.docs : (Array.isArray(prRaw.files) ? prRaw.files as {name:string;s3_key?:string}[] : []);
            bpFiles = prDocs.map((d: Record<string, unknown>) => ({ label: (d.label as string) || (d.name as string) || '', name: (d.name as string) || '', s3_key: (d.s3_key as string) || '' }));
            if (!bpPrompt) bpPrompt = pr.prompt || '';
          }
        }
        newConfigs[key] = { bizplan: {
          presetId: (raw.presetId as string) || '',
          files: bpFiles, extraPrompt: bpPrompt,
          phase: 'settings' as const,
        } as AIBizplanConfig };
      }
    });
    if (Object.keys(newConfigs).length > 0) {
      setStepConfigs(p => ({ ...p, ...newConfigs }));
    }
  }

  function addStep(tdId: string) {
    setDecisions(p => p.map(d => d.id === tdId ? { ...d, steps: [...d.steps, { name: '', type: 'work', due: '' }] } : d));
  }
  function updStep(tdId: string, idx: number, key: string, value: string) {
    setDecisions(p => p.map(d => d.id === tdId ? { ...d, steps: d.steps.map((s, i) => i === idx ? { ...s, [key]: value } : s) } : d));
  }
  function removeStep(tdId: string, idx: number) {
    setDecisions(p => p.map(d => d.id === tdId ? { ...d, steps: d.steps.filter((_, i) => i !== idx) } : d));
    // stepConfigs 키 시프트: idx 삭제 → idx 제거 후 idx보다 큰 인덱스를 1씩 당김 (orphan 방지)
    setStepConfigs(prev => {
      const prefix = tdId + ':';
      const next: typeof prev = {};
      Object.keys(prev).forEach(k => {
        if (!k.startsWith(prefix)) { next[k] = prev[k]; return; }
        const i = parseInt(k.split(':')[1]);
        if (i === idx) return;            // 삭제된 단계 config 제거
        if (i > idx) next[prefix + (i - 1)] = prev[k];  // 한 칸 당김
        else next[k] = prev[k];           // idx 이전은 그대로
      });
      return next;
    });
  }

  // drag-and-drop for steps
  const dragStepRef = useRef<{ dId: string; si: number } | null>(null);
  function reorderSteps(dId: string, from: number, to: number) {
    if (from === to) return;
    setDecisions(p => p.map(d => {
      if (d.id !== dId) return d;
      const arr = [...d.steps];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...d, steps: arr };
    }));
    // stepConfigs 키도 재정렬
    setStepConfigs(prev => {
      const next: typeof prev = {};
      const keys = Object.keys(prev);
      // 이 decision의 config만 재배치
      const prefix = dId + ':';
      const others = keys.filter(k => !k.startsWith(prefix));
      others.forEach(k => { next[k] = prev[k]; });
      // 현재 decision의 config 수집
      const cfgArr: (typeof prev[string] | undefined)[] = [];
      const maxIdx = Math.max(...keys.filter(k => k.startsWith(prefix)).map(k => parseInt(k.split(':')[1])), -1);
      for (let i = 0; i <= maxIdx; i++) cfgArr.push(prev[prefix + i]);
      // 재배치
      const reordered = [...cfgArr];
      const [movedCfg] = reordered.splice(from, 1);
      reordered.splice(to, 0, movedCfg);
      reordered.forEach((cfg, i) => { if (cfg) next[prefix + i] = cfg; });
      return next;
    });
  }

  // ── file handlers (for consultFields) ──
  async function handleFileUpload(fileList: FileList) {
    if (!fileList.length || !consultationId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (let i = 0; i < fileList.length; i++) fd.append('files', fileList[i]);
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/consultations/${consultationId}/files`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || '업로드 실패');
      setFiles(json.data?.files || []);
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '업로드 실패'); }
    finally { setUploading(false); }
  }
  async function handleFileDelete(idx: number) {
    if (!consultationId) return;
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/consultations/${consultationId}/files/${idx}`, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || '삭제 실패');
      setFiles(json.data?.files || []);
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '삭제 실패'); }
  }

  // ── save ──
  async function handleSave() {
    // onBlur 미완료 입력값 강제 반영
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    await new Promise(r => setTimeout(r, 100));

    // blur로 반영된 최신 state를 ref에서 읽음 (closure 변수는 stale)
    const curDecisions = decisionsRef.current;
    const curStepConfigs = stepConfigsRef.current;

    // 유효성 검사
    if (showConsultFields && !content.trim()) { onToast('상담 내용을 입력해주세요'); return; }
    for (let di = 0; di < curDecisions.length; di++) {
      const dec = curDecisions[di];
      if (dec.venture) {   // 벤처인증(투자) — 업무 유형 · 단계는 벤처 모듈이 정한다
        if (dec.venture.role === 'gp' && !dec.venture.projectId && !String(dec.venture.org || '').trim()) { onToast(`업무 ${di + 1} — 새 조합 이름을 적어 주세요`); return; }
        if (!String(dec.venture.investPlan || '').trim()) { onToast(`업무 ${di + 1} — 투자 처리 방법을 골라 주세요 (모르면 「미정」)`); return; }
        continue;
      }
      if (taskCategories.length > 0 && (!dec.category_path || dec.category_path.length === 0 || !dec.category_path[0])) { onToast(`업무 ${di + 1}의 업무 유형을 선택해주세요`); return; }
      if (dec.mode === 'custom' && !dec.customName.trim()) { onToast(`업무 ${di + 1}의 업무명을 입력해주세요`); return; }
      if (dec.mode === 'template' && !dec.templateId) { onToast(`업무 ${di + 1}의 템플릿을 선택해주세요`); return; }
      for (let si = 0; si < dec.steps.length; si++) {
        if (!dec.steps[si].name.trim()) { onToast(`업무 ${di + 1}의 단계 ${si + 1} 이름을 입력해주세요`); return; }
        if (dec.steps[si].type === 'collect' && !curStepConfigs[configKey(dec.id, si)]?.link) {   // 기존 자료 요청을 이으면 항목 설정은 필요 없음
          const cfg = curStepConfigs[configKey(dec.id, si)]?.collect;
          if (cfg && cfg.items && cfg.items.length === 0) { onToast('자료수집 항목을 1개 이상 추가해주세요'); return; }
        }
      }
    }
    // Save as template if requested
    for (const d of curDecisions) {
      if (d.saveAsTemplate && d.customName?.trim()) {
        try {
          // 업무 유형도 함께(전에는 빠져서 새 템플릿이 늘 「기타」로 갔다 · 2026-10-29)
          const catIds = (d.category_path || []).filter(Boolean);
          const r = await api.createTaskTemplate({
            name: d.customName.trim(), icon: d.customIcon || '📋',
            cat_path: catNamesOf(catIds), category_id: catIds.length ? catIds[catIds.length - 1] : null,
            steps: toTplSteps(d, curStepConfigs),
          });
          if (r && r.data && r.data.id) putTpl(r.data);
        } catch (e) { console.error('템플릿 저장 실패', e); }
      }
    }
    // Attach step configs (collect/ai/bizplan) to each decision's steps as extras
    const enrichedDecisions = curDecisions.map(dec => ({
      ...dec,
      steps: dec.steps.map((s, si) => {
        const key = configKey(dec.id, si);
        const cfg = curStepConfigs[key];
        if (!cfg) return s;
        const extras: Record<string, unknown> = {};
        if (s.type === 'collect' && cfg.link) extras.link_project_id = cfg.link.id;   // 기존 자료 요청 잇기 — 서버가 이 단계에 잇는다(2026-10-28)
        else if (s.type === 'collect' && cfg.collect) extras.collectConfig = cfg.collect;
        if (s.type === 'ai_questionnaire' && cfg.ai) extras.aiConfig = cfg.ai;
        if (s.type === 'ai_bizplan' && cfg.bizplan) extras.bizplanConfig = cfg.bizplan;
        return Object.keys(extras).length > 0 ? { ...s, extras } : s;
      }),
    }));
    onDone({
      content: showConsultFields ? content : undefined,
      files: showConsultFields ? files : undefined,
      tags: showConsultFields ? consultTags : undefined,
      decisions: enrichedDecisions,
      autoStartCollect: pendingAutoStartRef.current || undefined, // V22_COLLECT_START:22-Bc
    });
    pendingAutoStartRef.current = null;
  }

  // ── AI helpers ──
  function isAiType(type: string) { return type === 'ai_questionnaire' || type === 'ai_bizplan'; }

  // ── step config helpers ──
  function configKey(dId: string, si: number) { return `${dId}:${si}`; }
  function toggleStepConfig(dId: string, si: number, type: string) {
    const cur = expandedStepConfig;
    if (cur && cur.dId === dId && cur.si === si) setExpandedStepConfig(null);
    else setExpandedStepConfig({ dId, si, type });
  }
  function getCollectConfig(dId: string, si: number): CollectStepConfig {
    return stepConfigs[configKey(dId, si)]?.collect || { items: [], refFiles: [], refLinks: [], baseTemplateId: '' };
  }
  function setCollectConfig(dId: string, si: number, v: CollectStepConfig) {
    setStepConfigs(p => ({ ...p, [configKey(dId, si)]: { ...p[configKey(dId, si)], collect: v } }));
    markDirty(dId);
  }
  function getAiConfig(dId: string, si: number): AIStepConfig {
    return stepConfigs[configKey(dId, si)]?.ai || { presetId: '', files: [], extraPrompt: '', phase: 'settings', result: '' };
  }
  function setAiConfig(dId: string, si: number, v: AIStepConfig) {
    setStepConfigs(p => ({ ...p, [configKey(dId, si)]: { ...p[configKey(dId, si)], ai: v } }));
    markDirty(dId);
  }
  function getBizplanConfig(dId: string, si: number): AIBizplanConfig {
    return stepConfigs[configKey(dId, si)]?.bizplan || { presetId: '', files: [], extraPrompt: '', phase: 'settings' };
  }
  function setBizplanConfig(dId: string, si: number, v: AIBizplanConfig) {
    setStepConfigs(p => ({ ...p, [configKey(dId, si)]: { ...p[configKey(dId, si)], bizplan: v } }));
    markDirty(dId);
  }

  // ── category tree ──
  function buildCategoryTree() {
    const roots: TaskCategory[] = [];
    const childMap = new Map<string, TaskCategory[]>();
    for (const cat of taskCategories) {
      if (!cat.parent_id) roots.push(cat);
      else {
        const arr = childMap.get(cat.parent_id) || [];
        arr.push(cat);
        childMap.set(cat.parent_id, arr);
      }
    }
    roots.sort((a, b) => a.sort_order - b.sort_order);
    Array.from(childMap.values()).forEach(arr => arr.sort((a, b) => a.sort_order - b.sort_order));
    return { roots, childMap };
  }
  const { roots: catRoots, childMap: catChildMap } = buildCategoryTree();

  function getCascadingOptions(path: string[]): TaskCategory[][] {
    const levels: TaskCategory[][] = [catRoots];
    for (const selectedId of path) {
      const children = catChildMap.get(selectedId) || [];
      if (children.length === 0) break;
      levels.push(children);
    }
    return levels;
  }
  function handleCategorySelect(dId: string, depth: number, catId: string) {
    setDecisions(p => p.map(d => {
      if (d.id !== dId) return d;
      const newPath = [...(d.category_path || []).slice(0, depth), catId];
      return { ...d, category_path: newPath };
    }));
    markDirty(dId);
  }

  // ── styles ──
  const btnPrimary = (disabled?: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: disabled ? S.muted : S.accent, color: S.white,
    fontSize: T.sm, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  });
  const btnSecondary: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${S.accent}`,
    background: S.white, color: S.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer',
  };
  const btnGhost: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 8, border: `1px solid ${S.border}`,
    background: S.white, color: S.sub, fontSize: T.sm, fontWeight: 500, cursor: 'pointer',
  };
  const sectionLabel: React.CSSProperties = {
    fontSize: T.xs, fontWeight: 700, color: S.sub, display: 'block', marginBottom: 6,
    textTransform: 'uppercase' as const, letterSpacing: '0.03em',
  };
  const inputBase: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${S.border}`,
    fontSize: T.sm, color: S.body, outline: 'none', boxSizing: 'border-box' as const,
  };

  const innerContent = (
    <>
      {/* ── 헤더 — panelMode에서는 숨김 ── */}
      {!panelMode && (
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${S.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius: '12px 12px 0 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={onCancel} style={{ fontSize: 22, color: '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
          <div style={{ fontSize: T.lg, fontWeight: 700, color: S.white }}>{title}</div>
        </div>
        <button onClick={onCancel} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: T.sm, color: S.white, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      </div>
      )}

      {/* ── 본문 ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: panelMode ? '6px 16px 16px' : '20px 24px' }}>

          {/* headerExtra — panelMode에서는 진행할 업무 헤더에 통합 */}
          {!panelMode && headerExtra}

          {/* 상담 내용 (optional) */}
          {showConsultFields && (
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={sectionLabel}>상담 내용</label>
                <textarea value={content} onChange={e => setContent(e.target.value)} rows={2}
                  placeholder="고객 상황, 요청사항, 진행방향 등"
                  style={{ ...inputBase, resize: 'vertical', lineHeight: 1.6 }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={sectionLabel}>파일 첨부</label>
                <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files) handleFileUpload(e.target.files); e.target.value = ''; }} />
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = S.accent; e.currentTarget.style.background = S.accentLt; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.background = S.white; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = S.border; e.currentTarget.style.background = S.white; if (e.dataTransfer.files.length) handleFileUpload(e.dataTransfer.files); }}
                  style={{ padding: '12px 16px', borderRadius: 8, border: `1.5px dashed ${S.border}`, textAlign: 'center', cursor: 'pointer', color: S.muted, fontSize: T.xs, transition: 'all 0.15s', background: S.white }}>
                  {uploading ? '업로드 중...' : '📎 클릭 또는 드래그하여 파일 첨부'}
                </div>
                {files.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {files.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: S.sectionBg, border: `1px solid ${S.border}` }}>
                        <span style={{ fontSize: T.xs, color: S.sub }}>📄</span>
                        <span style={{ flex: 1, fontSize: T.xs, color: S.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <button onClick={() => handleFileDelete(i)} style={{ background: 'none', border: 'none', color: S.muted, cursor: 'pointer', fontSize: T.xs, padding: 2 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── 진행할 업무 섹션 ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, ...(panelMode ? { background: S.sectionBg || '#F3F4F6', margin: '-6px -16px 12px', padding: '10px 16px', borderBottom: `1px solid ${S.border}` } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: T.md, fontWeight: 700, color: S.title }}>진행할 업무</span>
                {decisions.length > 0 && (
                  <Badge bg={S.accentLt} color={S.accent} style={{ fontSize: T.xs, fontWeight: 700 }}>{decisions.length}</Badge>
                )}
              </div>
              {panelMode && headerExtra}
            </div>

            {/* 업무 추가 버튼 — 콤팩트 */}
            <div style={{ display: 'flex', gap: 6, marginBottom: decisions.length > 0 ? 12 : 0, alignItems: 'center' }}>
              <button onClick={addTemplateTask}
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${S.accent}`, background: S.accentLt, color: S.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                📋 템플릿
              </button>
              <button onClick={addCustomTask}
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${S.border}`, background: S.white, color: S.sub, fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                ✏️ 직접 입력
              </button>
              <button onClick={() => addVentureTask('gp')} title="대표님이 조합장(GP)으로 새 개인투자조합을 만들어 이 회사에 투자 — 벤처인증 진행현황에 새 조합으로 등록"
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${S.purple}55`, background: S.purpleLt, color: S.purple, fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                🚀 벤처 GP · 새 조합
              </button>
              <button onClick={() => addVentureTask('lp')} title="다른 조합에 LP(조합원)로 참여 — 조합을 고르면 그 조합의 투자법인으로 · 아직 미정이면 LP 후보로"
                style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${S.purple}55`, background: S.white, color: S.purple, fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                🚀 벤처 LP · 조합 참여
              </button>
            </div>

            {/* ── 각 업무 카드 ── */}
            {decisions.map((d, dIdx) => {
              if (d.venture) {
                const v = d.venture, gp = v.role === 'gp';
                const inp = { width: '100%', padding: '6px 8px', borderRadius: 5, border: `0.5px solid ${S.border}`, fontSize: 12, outline: 'none', background: S.white, color: S.body, boxSizing: 'border-box' as const };
                return (
                  <div key={d.id} style={{ borderRadius: 8, marginTop: dIdx > 0 ? 10 : 6, border: `1px solid ${S.purple}55`, background: S.white, overflow: 'hidden' }}>
                    {coPick(d)}
                    <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, background: S.purpleLt, borderBottom: `0.5px solid ${S.border}` }}>
                      <span style={{ fontSize: 14 }}>🚀</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, color: S.purple, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>벤처기업 확인 (투자형, {gp ? 'GP' : 'LP'}) <span style={{ fontWeight: 500 }}>{v.projectId ? (gp ? `조합 연결 ${v.projectOrg || ''}` : `조합 참여 ${v.projectOrg || ''}`) : (gp ? '새 조합' : '조합 미정')}</span></span>
                      <div role="radiogroup" aria-label="참여 방식" style={{ display: 'inline-flex', border: `0.5px solid ${S.border}`, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                        {(['gp', 'lp'] as const).map(r => (
                          <button key={r} type="button" aria-pressed={v.role === r} onClick={() => updVenture(d.id, r === 'gp' ? { role: r, org: v.org || (coNameOf(d) ? defaultOrg(coNameOf(d)) : '') } : { role: r })}
                            style={{ padding: '3px 10px', border: 'none', background: v.role === r ? S.purple : S.white, color: v.role === r ? '#fff' : S.sub, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{r.toUpperCase()}</button>
                        ))}
                      </div>
                      <button onClick={() => removeDecision(d.id)}
                        style={{ background: 'none', border: `0.5px solid ${S.red}33`, color: S.red, cursor: 'pointer', fontSize: 11, padding: '3px 8px', borderRadius: 4, flexShrink: 0 }}>삭제</button>
                    </div>
                    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {!v.projectId ? (   // 진행 주체 — 새 조합 · LP 후보만(고른 조합에 참여하면 그 조합의 주체를 따른다)
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: S.sub }}>진행 주체</span>
                          <div role="radiogroup" aria-label="진행 주체" style={{ display: 'inline-flex', border: `0.5px solid ${S.border}`, borderRadius: 6, overflow: 'hidden' }}>
                            {OFFICES.map(o => (
                              <button key={o.v} type="button" aria-pressed={(v.office || 'roum') === o.v} title={o.name} onClick={() => updVenture(d.id, { office: o.v })}
                                style={{ padding: '4px 12px', border: 'none', background: (v.office || 'roum') === o.v ? S.purple : S.white, color: (v.office || 'roum') === o.v ? '#fff' : S.sub, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{o.short}</button>
                            ))}
                          </div>
                          <span style={{ fontSize: 11, color: S.muted }}>{(v.office || 'roum') === 'atv' ? '올댓벤처스 어드민 진행현황에도 보입니다' : '로움 진행현황에만'}</span>
                        </div>
                      ) : null}
                      {gp ? (
                        <>
                          {(() => {
                            const own = ownRows(d);
                            if (!own.length) return null;
                            return (
                              <div style={{ padding: '8px 10px', borderRadius: 6, background: '#F5F3FF', border: `0.5px solid ${S.purple}44`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: S.purple }}>벤처인증에 이미 등록된 조합이 있어요</span>
                                <select value={v.projectId || ''} onChange={e => { const id = e.target.value; const row = own.find(x => x.id === id); updVenture(d.id, { projectId: id, projectOrg: row ? row.org : '' }); }} style={inp}>
                                  {own.map(r => <option key={r.id} value={r.id}>{r.org || '(이름 없음)'} · {r.step || 1}단계{r.office === 'atv' ? ' · 올댓벤처스' : ' · 로움'} — 여기에 연결</option>)}
                                  <option value="">새 조합을 따로 만들기</option>
                                </select>
                                <span style={{ fontSize: 11, color: S.muted }}>{v.projectId ? '새 조합을 만들지 않고, 이 업무를 그 조합 진행에 연결해요. 벤처 화면에서 만든 같은 업무가 있으면 하나로 합쳐요.' : '이미 있는 조합과 별개로 새 조합을 하나 더 만들어요.'}</span>
                              </div>
                            );
                          })()}
                          {!v.projectId && (<>
                          <label style={{ fontSize: 11, fontWeight: 600, color: S.sub }}>새 조합 이름 <span style={{ color: S.red }}>*</span></label>
                          <input value={v.org || ''} onChange={e => updVenture(d.id, { org: e.target.value })} placeholder="예: ○○개인투자조합1호" style={inp} />
                          <span style={{ fontSize: 11, color: S.muted }}>저장하면 벤처인증 › 진행현황 1단계에 이 조합이 생기고, 이 회사가 투자법인 · 대표님이 조합장(GP)으로 들어갑니다. 이름은 벤처 화면에서도 고칠 수 있어요.</span>
                          </>)}
                        </>
                      ) : (
                        <>
                          <label style={{ fontSize: 11, fontWeight: 600, color: S.sub }}>참여할 조합</label>
                          <select value={v.projectId || ''} onChange={e => { const id = e.target.value; const row = (ventureRows || []).find(x => x.id === id); updVenture(d.id, { projectId: id, projectOrg: row ? row.org : '' }); }} style={inp}>
                            <option value="">아직 미정 — LP 후보로 올려 두기</option>
                            {(ventureRows || []).map(r => <option key={r.id} value={r.id}>{r.org || '(이름 없음)'} · {r.step || 1}단계{r.office === 'atv' ? ' · 올댓벤처스' : ' · 로움'}</option>)}
                          </select>
                          <span style={{ fontSize: 11, color: S.muted }}>{v.projectId ? '저장하면 이 회사가 그 조합의 투자법인으로 들어가고 대표님은 LP(조합원)로 표시됩니다 — 단계는 그 조합과 같이 움직여요.' : ventureRows === null ? '조합 목록을 불러오는 중…' : '저장하면 벤처인증 › LP 후보에 이 회사 · 대표님 · 소개 팀장님이 올라가요. 조합이 정해지면 LP 후보에서 매칭하세요.'}</span>
                        </>
                      )}
                      {/* 투자 처리 방법 (2026-10-18 지도사 「투자를 어떻게 하기로 했는지도 여기 적을 수 있게」) — 벤처 등록 화면 「투자 처리 방법 *」과 같은 칸(조합 투자법인 줄 · LP 후보 메모로 감) · 우리만 봄 · 모르면 「미정」 */}
                      <label style={{ fontSize: 11, fontWeight: 600, color: S.sub, marginTop: 4 }}>투자 처리 방법 <span style={{ color: S.red }}>*</span> <span style={{ fontWeight: 400, color: S.muted }}>{v.projectId ? '그 조합에 이미 적혀 있으면 그대로 둬요 (조합 단위 · 우리만 봐요)' : '이 조합의 투자를 어떻게 하기로 했는지 (조합 단위 · 우리만 봐요)'}</span></label>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {INVEST_PLANS.map(t => {
                          const on = (v.investPlan || '').trim() === t;
                          return <button key={t} type="button" aria-pressed={on} onClick={() => updVenture(d.id, { investPlan: t })}
                            style={{ height: 26, padding: '0 10px', borderRadius: 13, border: `0.5px solid ${on ? S.purple : S.border}`, background: on ? '#F3E8FF' : S.white, color: on ? S.purple : S.sub, fontSize: 11, fontWeight: on ? 700 : 500, cursor: 'pointer' }}>{t}</button>;
                        })}
                      </div>
                      <textarea rows={2} maxLength={500} value={v.investPlan || ''} onChange={e => updVenture(d.id, { investPlan: e.target.value })}
                        placeholder="위에서 고르거나 직접 적어 주세요. 예) 전환사채 1억, 확인서 받은 뒤 바로 회수"
                        style={{ ...inp, height: 'auto', minHeight: 48, padding: '6px 10px', resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit', border: `0.5px solid ${(v.investPlan || '').trim() ? S.border : '#F59E0B'}` }} />
                    </div>
                  </div>
                );
              }
              const isTemplate = d.mode === 'template';
              const isFpWait = d.taskStatus === 'FP요청대기';
              return (
                <div key={d.id} style={{
                  borderRadius: 8, marginTop: dIdx > 0 ? 10 : 6,
                  border: `1px solid ${isFpWait ? S.purple : '#85B7EB'}`,
                  background: S.white, overflow: 'hidden',
                }}>
                  {coPick(d)}
                  {/* 헤더 (현황 카드와 동일 구조) */}
                  <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, background: S.sectionBg, borderBottom: `0.5px solid ${S.border}` }}>
                    {isTemplate ? (
                      <span style={{ fontSize: 13 }}>📋</span>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <button onClick={() => setExpandEmojiPicker(expandEmojiPicker === d.id ? null : d.id)}
                          style={{ width: 28, height: 28, borderRadius: 6, border: `0.5px solid ${S.border}`, background: S.white, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {d.customIcon}
                        </button>
                        {expandEmojiPicker === d.id && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10, marginTop: 4, padding: 8, borderRadius: 10, background: S.white, border: `1px solid ${S.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, width: 180 }}>
                            {EMOJI_LIST.map(em => (
                              <button key={em} onClick={() => { updDecision(d.id, 'customIcon', em); setExpandEmojiPicker(null); }}
                                style={{ width: 32, height: 32, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = S.sectionBg; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                                {em}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isTemplate ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {tplEdit && tplEdit.dId === d.id ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <input autoFocus value={tplEdit.value} disabled={tplEdit.busy} aria-label="템플릿 이름"
                                onChange={e => setTplEdit({ ...tplEdit, value: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void saveTplName(); } else if (e.key === 'Escape') { e.preventDefault(); setTplEdit(null); } }}
                                style={{ flex: 1, minWidth: 0, padding: '4px 8px', borderRadius: 5, border: `1px solid ${S.accent}`, fontSize: 12, fontWeight: 600, color: S.body, outline: 'none', background: S.white }} />
                              <button type="button" onClick={() => void saveTplName()} disabled={tplEdit.busy}
                                style={{ ...tplBtn, background: S.accent, color: '#fff', border: `1px solid ${S.accent}` }}>{tplEdit.busy ? '저장 중' : '저장'}</button>
                              <button type="button" onClick={() => setTplEdit(null)} disabled={tplEdit.busy} style={tplBtn}>취소</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <TemplatePicker templates={templates} value={d.templateId} nameOf={tplName}
                                onPick={id => selectTemplate(d.id, id)} onCreate={name => startNewTemplate(d.id, name)} />
                              {d.templateId && (
                                <button type="button" title="템플릿 이름 고치기 — 다음 등록부터 이 이름으로 보여요"
                                  onClick={() => { const t = templates.find(x => x.id === d.templateId); if (t) setTplEdit({ dId: d.id, tplId: t.id, value: tplName(t), busy: false }); }}
                                  style={tplBtn}>이름 고치기</button>
                              )}
                            </div>
                          )}
                          {d.templateId && (
                            <input value={d.customName} onChange={e => updDecision(d.id, 'customName', e.target.value)}
                              placeholder="업무명 (템플릿명과 다르게 지정 가능)"
                              style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', borderRadius: 5, border: `0.5px solid ${S.border}`, fontSize: 11, outline: 'none', background: S.white, color: S.sub }} />
                          )}
                        </div>
                      ) : (
                        <input value={d.customName} onChange={e => updDecision(d.id, 'customName', e.target.value)}
                          placeholder="업무명을 입력하세요"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', borderRadius: 5, border: `0.5px solid ${S.border}`, fontSize: 12, outline: 'none', background: S.white, color: S.body }} />
                      )}
                    </div>
                    <button onClick={() => removeDecision(d.id)}
                      style={{ background: 'none', border: `0.5px solid ${S.red}33`, color: S.red, cursor: 'pointer', fontSize: 11, padding: '3px 8px', borderRadius: 4, flexShrink: 0 }}>삭제</button>
                  </div>

                  {/* 직접입력: 템플릿 저장 체크 */}
                  {!isTemplate && (
                    <div style={{ padding: '8px 14px', borderBottom: `0.5px solid ${S.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: S.sub, cursor: 'pointer' }}>
                        <input type="checkbox" checked={d.saveAsTemplate}
                          onChange={e => updDecision(d.id, 'saveAsTemplate', e.target.checked)}
                          style={{ accentColor: S.accent }} />
                        저장할 때 템플릿으로도 만들기
                      </label>
                      {!d.venture && <button type="button" onClick={() => backToTemplate(d.id)} style={tplBtn}>템플릿에서 고르기</button>}
                    </div>
                  )}

                  {/* ── 업무 유형 (카테고리) ── */}
                  {taskCategories.length > 0 && (
                    <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', borderBottom: '0.5px solid ' + S.border }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: S.sub, flexShrink: 0 }}>업무 유형 <span style={{ color: S.red }}>*</span></span>
                      {getCascadingOptions(d.category_path || []).map((options, depth) => (
                        <select key={depth} value={(d.category_path || [])[depth] || ''}
                          onChange={e => handleCategorySelect(d.id, depth, e.target.value)}
                          style={{ padding: '5px 8px', borderRadius: 5, border: `1px solid ${S.border}`, fontSize: 11, color: S.body, outline: 'none', background: S.white, minWidth: 100, cursor: 'pointer' }}>
                          <option value="">{depth === 0 ? '선택' : '하위'}</option>
                          {options.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                        </select>
                      ))}
                    </div>
                  )}

                  {/* ── 하위 단계 ── */}
                  {d.steps.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: S.sub, padding: '8px 14px', borderBottom: `0.5px solid ${S.border}` }}>하위 단계</div>
                      <div>
                        {d.steps.map((s, si) => {
                          const isAi = isAiType(s.type);
                          const presetName = s.aiPresetId ? aiPresets.find(p => p.id === s.aiPresetId)?.name : null;
                          const typeInfo = getStepTypeInfo(s.type);
                          const typeMenuKey = `${d.id}:${si}`;
                          const isTypeMenuOpen = expandStepTypeMenu === typeMenuKey;
                          const isExpanded = expandedStepConfig?.dId === d.id && expandedStepConfig?.si === si;

                          return (
                            <div key={si} draggable
                              onDragStart={() => { dragStepRef.current = { dId: d.id, si }; }}
                              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderTop = '2px solid #2E75B6'; }}
                              onDragLeave={e => { e.currentTarget.style.borderTop = ''; }}
                              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderTop = ''; if (dragStepRef.current && dragStepRef.current.dId === d.id) { reorderSteps(d.id, dragStepRef.current.si, si); } dragStepRef.current = null; }}
                              onDragEnd={() => { dragStepRef.current = null; }}
                              style={{ padding: '7px 14px', borderBottom: `0.5px solid ${S.border}`, cursor: 'grab' }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ cursor: 'grab', color: S.muted, fontSize: 12, flexShrink: 0, userSelect: 'none' as const }}>∷</span>
                                <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, background: S.accent, color: S.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{si + 1}</span>
                                <IMEInput value={s.name} onChange={v => updStep(d.id, si, 'name', v)}
                                  placeholder={`단계 ${si + 1}`}
                                  style={{ flex: 1, padding: '4px 8px', borderRadius: 5, border: `0.5px solid ${S.border}`, fontSize: 12, color: S.body, outline: 'none', background: S.white }} />
                                <DueDatePicker value={s.due} onChange={v => updStep(d.id, si, 'due', v)} placeholder="기한" style={{ minWidth: 100, flexShrink: 0 }} />
                                {/* Type badge */}
                                <div style={{ position: 'relative' }}>
                                  <button onClick={() => setExpandStepTypeMenu(isTypeMenuOpen ? null : typeMenuKey)}
                                    style={{ padding: '3px 7px', borderRadius: 5, fontSize: 11, fontWeight: 500, cursor: 'pointer', border: '0.5px solid transparent', background: typeInfo.bg, color: typeInfo.color, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                                    <span style={{ fontSize: 11 }}>{typeInfo.icon}</span> {typeInfo.label}
                                  </button>
                                  {isTypeMenuOpen && (
                                    /* V20_MASTER:5 — bottom:100%로 위로 펼침 (모달 하단 클리핑 회피) */
                                    <div style={{ position: 'absolute', right: 0, bottom: '100%', marginBottom: 4, zIndex: 20, background: S.white, borderRadius: 10, border: `1px solid ${S.border}`, boxShadow: '0 -8px 24px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden' }}>
                                      {STEP_TYPE_CONFIG.map(tc => {
                                        const isAiDisabled = tc.key === 'ai_questionnaire' || tc.key === 'ai_bizplan';
                                        return (
                                        <button key={tc.key} onClick={() => { if (isAiDisabled) return; updStep(d.id, si, 'type', tc.key); setExpandStepTypeMenu(null); }}
                                          style={{ width: '100%', padding: '7px 12px', border: 'none', background: s.type === tc.key ? tc.bg : S.white, display: 'flex', alignItems: 'center', gap: 8, cursor: isAiDisabled ? 'not-allowed' : 'pointer', fontSize: T.xs, color: isAiDisabled ? '#D1D5DB' : (s.type === tc.key ? tc.color : S.body), opacity: isAiDisabled ? 0.5 : 1, fontWeight: s.type === tc.key ? 600 : 400, borderBottom: `1px solid ${S.border}`, transition: 'background 0.1s' }}
                                          onMouseEnter={e => { if (s.type !== tc.key) (e.currentTarget as HTMLButtonElement).style.background = S.sectionBg; }}
                                          onMouseLeave={e => { if (s.type !== tc.key) (e.currentTarget as HTMLButtonElement).style.background = S.white; }}>
                                          <span style={{ fontSize: T.md }}>{tc.icon}</span>
                                          <span>{tc.label}{isAiDisabled ? ' (준비중)' : ''}</span>
                                        </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <button onClick={() => removeStep(d.id, si)}
                                  style={{ background: 'none', border: 'none', color: S.muted, cursor: 'pointer', fontSize: T.xs, padding: '2px 4px', borderRadius: 4 }}>✕</button>
                              </div>

                              {/* Collect settings toggle */}
                              {s.type === 'collect' && (
                                <div style={{ marginTop: 6, marginLeft: 26 }}>
                                  {/* 기존 자료 요청 잇기(2026-10-28 지도사) — 이 업체에 업무에 안 이어진 자료수집이 있을 때만 */}
                                  <div style={{ marginBottom: 6 }}>
                                    <CollectLinkPicker companyId={coIdOf(d)} value={stepConfigs[configKey(d.id, si)]?.link?.id || ''}
                                      onChange={(id, label) => setStepConfigs(p => ({ ...p, [configKey(d.id, si)]: { ...p[configKey(d.id, si)], link: id ? { id, label } : undefined } }))} />
                                  </div>
                                  {stepConfigs[configKey(d.id, si)]?.link
                                    ? <div style={{ fontSize: T.xs, color: '#2563EB', lineHeight: 1.5 }}>「{stepConfigs[configKey(d.id, si)]?.link?.label}」 자료 요청을 이 단계에 이어요 · 새로 만들지 않아요(보낸 링크 · 받은 자료 그대로)</div>
                                    : <>
                                  <button onClick={() => toggleStepConfig(d.id, si, 'collect')}
                                    style={{ padding: '5px 12px', borderRadius: 6, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', border: `1px solid #2563EB`, background: isExpanded ? '#DBEAFE' : '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span>📂</span> 자료수집 설정 {isExpanded ? '▲' : '▼'}
                                  </button>
                                  {isExpanded && (
                                    <div style={{ marginTop: 8, padding: 12, borderRadius: 8, border: `1px solid ${S.border}`, background: S.white }}>
                                      <CollectStepConfigPanel value={getCollectConfig(d.id, si)} onChange={v => setCollectConfig(d.id, si, v)} onToast={onToast}
                                        /* V22_COLLECT_START:22-Bd */
                                        onStartCollect={enableAutoStartCollect ? () => {
                                          if (!window.confirm('이 업무 정보를 저장하고 자료수집을 시작합니다. 계속하시겠습니까?')) return;
                                          pendingAutoStartRef.current = { dId: d.id, si };
                                          handleSave();
                                        } : undefined}
                                      />
                                    </div>
                                  )}
                                  </>}
                                </div>
                              )}

                              {/* AI settings toggle */}
                              {isAi && (
                                <div style={{ marginTop: 6, marginLeft: 26 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <button onClick={() => toggleStepConfig(d.id, si, s.type)}
                                      style={{ padding: '5px 12px', borderRadius: 6, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', border: `1px solid ${S.purple}`, background: isExpanded ? '#E9D5FF' : S.purpleLt, color: S.purple, display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span>🤖</span> {s.type === 'ai_questionnaire' ? 'AI 질문지 설정' : 'AI 사업계획서 설정'} {isExpanded ? '▲' : '▼'}
                                    </button>
                                    {presetName && <Badge bg={S.purpleLt} color={S.purple} style={{ fontSize: T.xs }}>{presetName}</Badge>}
                                  </div>
                                  {isExpanded && s.type === 'ai_questionnaire' && (
                                    <div style={{ marginTop: 8, padding: 12, borderRadius: 8, border: `1px solid ${S.border}`, background: S.white }}>
                                      <AIStepConfigPanel value={getAiConfig(d.id, si)} onChange={v => setAiConfig(d.id, si, v)} presets={aiPresets} aiType="ai_questionnaire" />
                                    </div>
                                  )}
                                  {isExpanded && s.type === 'ai_bizplan' && (
                                    <div style={{ marginTop: 8, padding: 12, borderRadius: 8, border: `1px solid ${S.border}`, background: S.white }}>
                                      <AIBizplanConfigPanel value={getBizplanConfig(d.id, si)} onChange={v => setBizplanConfig(d.id, si, v)} presets={aiPresets} />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Add step */}
                      <button onClick={() => addStep(d.id)}
                        style={{ width: '100%', padding: '6px', fontSize: 11, color: S.accent, cursor: 'pointer', background: 'transparent', border: 'none', fontWeight: 500, borderTop: `0.5px solid ${S.border}` }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = S.sectionBg; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                        + 단계 추가
                      </button>
                      {/* 고른 템플릿과 단계가 달라지면 — 이 업무에만 / 템플릿에도 (2026-10-29 지도사 「템플릿 편집도 여기서」) */}
                      {(() => {
                        const tpl = isTemplate ? templates.find(t => t.id === d.templateId) : undefined;
                        if (!tpl || !(stepsChanged(d, tpl) || tplDirty[d.id])) return null;
                        const ask = tplPush && tplPush.dId === d.id;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '8px 14px', borderTop: `0.5px solid ${S.border}`, background: ask ? '#FFFBEB' : S.sectionBg }}>
                            <span style={{ flex: 1, minWidth: 160, fontSize: 11, color: S.sub, lineHeight: 1.5 }}>
                              {tpl.system_key ? '템플릿과 달라졌어요 · 이 업무에만 들어가요(벤처 GP · LP 템플릿은 벤처 화면 기준이라 여기서 바꾸지 않아요)'
                                : ask ? `「${tplName(tpl)}」 템플릿 단계를 이대로 바꿀까요? 다음 업무부터 이 단계로 시작해요`
                                : '템플릿과 달라졌어요 · 지금은 이 업무에만 들어가요'}
                            </span>
                            {!tpl.system_key && (ask ? (
                              <>
                                <button type="button" disabled={tplPush ? tplPush.busy : false} onClick={() => void pushToTemplate(d)} style={{ ...tplBtn, background: S.accent, borderColor: S.accent, color: '#fff', minWidth: 72 }}>{tplPush && tplPush.busy ? '반영 중' : '바꾸기'}</button>
                                <button type="button" disabled={tplPush ? tplPush.busy : false} onClick={() => setTplPush(null)} style={{ ...tplBtn, minWidth: 72 }}>취소</button>
                              </>
                            ) : (
                              <>
                                <button type="button" onClick={() => setTplPush({ dId: d.id, busy: false })} style={{ ...tplBtn, color: S.accent, borderColor: S.accent, minWidth: 104 }}>템플릿에도 반영</button>
                                <button type="button" onClick={() => startNewTemplate(d.id, '')} style={{ ...tplBtn, minWidth: 104 }}>새 템플릿으로</button>
                              </>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* ── 착수방식 + 마감일 (한 줄) ── */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', background: S.sectionBg, borderTop: `0.5px solid ${S.border}` }}>
                    {([['진행', '▶ 바로착수'], ['FP요청대기', '⏳ FP요청대기']] as const).map(([val, label]) => {
                      const active = d.taskStatus === val;
                      return (
                        <button key={val} onClick={() => updDecision(d.id, 'taskStatus', val)}
                          style={{
                            padding: '5px 10px', borderRadius: 5, fontSize: 11, cursor: 'pointer',
                            fontWeight: active ? 600 : 400,
                            border: `0.5px solid ${active ? S.accent : S.border}`,
                            background: active ? S.accentLt : S.white,
                            color: active ? S.accent : S.body,
                            flexShrink: 0,
                          }}>
                          {label}
                        </button>
                      );
                    })}
                    <div style={{ flex: 1 }} />
                    {isFpWait ? (
                      <span style={{ fontSize: 11, color: S.purple, fontWeight: 500 }}>착수 시 설정</span>
                    ) : (
                      <DueDatePicker value={d.deadline} onChange={v => updDecision(d.id, 'deadline', v)} placeholder="마감일" style={{ width: 110, flexShrink: 0 }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* spacer */}
          <div style={{ height: 8 }} />
        </div>

        {/* ── 하단 버튼 ── */}
        <div style={{ padding: panelMode ? '10px 0' : '14px 24px', borderTop: panelMode ? 'none' : `1px solid ${S.border}`, display: 'flex', gap: 8, background: panelMode ? 'transparent' : S.sectionBg, borderRadius: panelMode ? 0 : '0 0 12px 12px' }}>
          {!panelMode && <button onClick={onCancel} style={btnGhost}>취소</button>}
          {!panelMode && <div style={{ flex: 1 }} />}
          <button onClick={handleSave} style={{ ...btnPrimary(), ...(panelMode ? { flex: 1 } : {}) }}>💾 저장</button>
          {consultationId && (
            <button onClick={() => { handleSave(); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: S.green, color: S.white, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>
              📨 FP 전송
            </button>
          )}
        </div>
      </>
  );

  if (panelMode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: S.white }}>
        {innerContent}
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onCancel} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.4)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', width: '100%', maxWidth: modalMaxWidth || 560, minHeight: 600, maxHeight: modalMaxHeight || '92vh', background: S.white, borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {innerContent}
      </div>
    </div>
  );
}
